import React, { useState, useEffect } from 'react';
import { XCircle, X } from 'lucide-react';
import './CancellationRequestModal.css';
import { getAuth } from 'firebase/auth';
// All cancellation logic is now handled by Cloud Function endpoint

function CancellationRequestModal({ isOpen, onClose, creatorData, onCancellationResponse }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      console.log('🔴 CancellationRequestModal opened:', {
        hasCreatorData: !!creatorData,
        creatorId: creatorData?.id,
        projectId: creatorData?.projectId,
        campaignId: creatorData?.campaignId,
        briefId: creatorData?.briefId,
        hasCancellationRequest: !!creatorData?.cancellationRequest,
        cancellationRequestStatus: creatorData?.cancellationRequest?.status,
        fullCreatorData: creatorData
      });
    }
  }, [isOpen, creatorData]);

  if (!isOpen || !creatorData || !creatorData.cancellationRequest || creatorData.cancellationRequest.status !== 'pending') {
    if (isOpen && !creatorData) {
      console.error('❌ CancellationRequestModal: No creatorData provided');
    } else if (isOpen && creatorData && !creatorData.cancellationRequest) {
      console.error('❌ CancellationRequestModal: No cancellationRequest in creatorData:', creatorData);
    } else if (isOpen && creatorData?.cancellationRequest?.status !== 'pending') {
      console.error('❌ CancellationRequestModal: Cancellation request status is not pending:', creatorData?.cancellationRequest?.status);
    }
    return null;
  }

  // Validate that projectId exists
  if (!creatorData.projectId) {
    console.error('❌ CancellationRequestModal: Missing projectId in creatorData:', creatorData);
    return (
      <div className="cancellation-request-modal-overlay" onClick={onClose}>
        <div className="cancellation-request-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="cancellation-request-modal-header">
            <h2>Error</h2>
            <button className="cancellation-request-modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className="cancellation-request-modal-body">
            <p>Project ID is required. Cannot process cancellation request without project ID.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleApprove = async () => {
    if (isLoading || isApproving) return;

    setIsLoading(true);
    setIsApproving(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Validate projectId
      if (!creatorData.projectId) {
        throw new Error('Project ID is required');
      }

      // Call Cloud Function endpoint to handle cancellation approval and refund
      const { approveCancellation } = await import('../services/apiService');
      
      console.log('🚀 CancellationRequestModal: Starting approval process', {
        brandUserId: user.uid,
        campaignId: creatorData.campaignId,
        briefId: creatorData.briefId,
        creatorId: creatorData.id,
        projectId: creatorData.projectId
      });
      
      const result = await approveCancellation({
        brandUserId: user.uid,
        campaignId: creatorData.campaignId,
        briefId: creatorData.briefId,
        creatorId: creatorData.id,
        projectId: creatorData.projectId
      });

      console.log('📥 CancellationRequestModal: Received API response:', {
        success: result.success,
        refundAmount: result.refundAmount,
        refundProcessed: result.refundProcessed,
        previousBalance: result.previousBalance,
        newBalance: result.newBalance,
        fullResponse: result
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to approve cancellation');
      }

      console.log('✅ CancellationRequestModal: Cancellation approved and refund processed:', result);

      // Call callback
      if (onCancellationResponse) {
        onCancellationResponse('approved');
      }

      onClose();
    } catch (error) {
      console.error('Error approving cancellation:', error);
      alert(`Failed to approve cancellation: ${error.message}`);
    } finally {
      setIsLoading(false);
      setIsApproving(false);
    }
  };

  // Notification is now handled by the Cloud Function endpoint


  const cancellationRequest = creatorData.cancellationRequest;

  return (
    <div className="cancellation-request-modal-overlay" onClick={onClose}>
      <div className="cancellation-request-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="cancellation-request-modal-header">
          <div className="cancellation-request-modal-icon">
            <XCircle size={24} />
          </div>
          <div className="cancellation-request-modal-header-text">
            <h2>Cancellation Request</h2>
            <p>{creatorData.briefName || 'Project'}</p>
          </div>
          <button className="cancellation-request-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="cancellation-request-modal-body">
          {/* Apology Message */}
          <div className="cancellation-request-apology">
            <p>We're sorry that the creator needs to cancel this project. We understand this can be frustrating.</p>
            <p className="cancellation-request-refund-info">
              <strong>Your credit will be automatically refunded to your wallet</strong> once you approve this cancellation request.
            </p>
          </div>

          <div className="cancellation-request-creator-info">
            <div className="cancellation-request-avatar">
              {creatorData.avatar ? (
                <img src={creatorData.avatar} alt={creatorData.name} />
              ) : (
                <div className="cancellation-request-avatar-placeholder">
                  {creatorData.name?.charAt(0) || 'C'}
                </div>
              )}
            </div>
            <div className="cancellation-request-creator-details">
              <h3>{creatorData.name || 'Creator'}</h3>
              <p>{creatorData.campaignName || 'Campaign'}</p>
            </div>
          </div>

          <div className="cancellation-request-info-box">
            {cancellationRequest.reason && (
              <div className="cancellation-request-info-item">
                <span className="cancellation-request-info-label">Reason:</span>
                <span className="cancellation-request-info-value">{cancellationRequest.reason}</span>
              </div>
            )}
            <div className="cancellation-request-info-item">
              <span className="cancellation-request-info-label">Requested At:</span>
              <span className="cancellation-request-info-value">
                {new Date(cancellationRequest.requestedAt).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="cancellation-request-actions">
            <button
              className="cancellation-request-btn cancellation-request-btn-approve"
              onClick={handleApprove}
              disabled={isLoading}
            >
              {isApproving ? 'Approving...' : 'Approve & Process Refund'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CancellationRequestModal;

